/**
 * Formats a monetary amount as a USD currency string
 * Handles null, undefined, empty strings, and NaN values gracefully
 * 
 * @param amount - The amount to format (can be string, number, null, or undefined)
 * @returns Formatted currency string (defaults to $0.00 for invalid values)
 */
export const formatCurrency = (amount: string | number | null | undefined): string => {
  // Handle null, undefined, or empty string
  if (amount === null || amount === undefined || amount === '') {
    return '$0.00'
  }
  
  // Convert to number
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  
  // Handle NaN or invalid numbers
  if (isNaN(num)) {
    return '$0.00'
  }
  
  // Format as USD currency
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num)
}

/**
 * Formats a percentage value
 * 
 * @param value - The percentage value to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number | null | undefined, decimals = 1): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%'
  }
  return `${value.toFixed(decimals)}%`
}

/**
 * Formats a date string for display
 * 
 * @param date - ISO date string or Date object
 * @returns Formatted date string
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return 'N/A'
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return 'N/A'
  }
}

/**
 * Gets the color scheme for a document status
 * 
 * @param status - The document status
 * @returns Joy UI color scheme
 */
export const getStatusColor = (status: string): any => {
  const statusColors: Record<string, string> = {
    draft: 'neutral',
    sent: 'primary',
    viewed: 'primary',
    accepted: 'success',
    paid: 'success',
    partially_paid: 'warning',
    pending: 'warning',
    overdue: 'danger',
    rejected: 'danger',
    cancelled: 'neutral',
    expired: 'warning',
    invoiced: 'success',
  }
  return statusColors[status] || 'neutral'
}

/**
 * Formats a status string for display
 * Converts snake_case to Title Case
 * 
 * @param status - The status string to format
 * @returns Formatted status string
 */
export const formatStatus = (status: string): string => {
  if (!status) return ''
  
  // Convert snake_case to Title Case
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}